/* eslint-disable no-unused-vars */
const Autodesk = window.Autodesk;
import store from '@/store';
import { removeFromScene, pointerToRaycaster, addToScene, zoomToFitBox2D } from '../utils/forgeUtils';
import { buildClusterLevels, clusterLevelFor, clustersAtLevel } from '../utils/clusterLevels';
import { ClusterCountLabels, viewerPxPerUnit } from './clusterCountLabels';
import { convexHull } from '../utils/geometryUtils';
import { disposeArea2d, drawArea2d, drawPolygon, drawLine } from '../utils/threejsUtils';
import { ForgeEquipment, ForgeScaffold, ForgeAnnotation, ForgeInspection, ForgePipeline, ForgeStructural, ForgeAvaInspect } from './../scripts/js/forgeModels';
import { getAMISTagById } from '@/utils/amisAPIs';
import config from '../../appConfig';
import { tagHandlers } from './tagHandlers/index';
import { textureManager } from './textureManager';
import { shaders } from './shaders';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { computeElevationPercentage, interpolateHeatColor } from '@/utils/elevationHeatmap';
import { applyViewerBackground } from '@/utils/viewerBackground';
const THREE = window.THREE;

// 2D plan 360 bubbles (AVA-633). Groups stay at least this far apart on screen, which is the
// diameter of a 2D cluster icon (gl_PointSize 80 in shaders.js), so bubbles do not overlap.
const BUBBLE_CLUSTER_RADIUS_PX = 80;
// Finest precomputed level; zooming past it shows every scan on its own.
const BUBBLE_MAX_LEVEL = 16;
// Below this view height (viewer units) every scan is shown on its own, as before AVA-633.
const BUBBLE_UNCLUSTER_VIEW_HEIGHT = 0.1;

/**
 * Extension for rendering 3D labels and points in the Forge viewer
 */
class labels3dExt extends Autodesk.Viewing.Extension {
    /**
     * Initialize extension
     * @param {Object} loadedViewer - Forge viewer instance
     * @param {Object} options - Extension options
     */
    constructor(loadedViewer, options) {
        super(loadedViewer, options);
        this.viewer = loadedViewer;
        
        // Image point cloud elements
        // 2D bubble levels: built once per data change, a zoom only picks a level
        this.bubbleLevels = null;
        this.bubbleLevelsStale = true;
        this.bubbleLevelsKey = null;
        this.bubbleDrawnKey = null;
        this.bubbleLevel = null;
        this.clusterHoveringAreaMesh = null;
        this.clusterPolyBorderGl = [];
        
        // Tags cloud elements
        this.pointClouds = [];
        this.selectedPcInterval = null;
        this.hoveringPointCloud = null;
        this.cameraZ = 0;

        // Appearance settings
        this.hoveringColor = 0x03624c;
        this.hoveringArea = null;
        this.camera = this.viewer.impl.camera;
        this.viewType = this.isViewer2d() ? '2d' : '3d';
        this.colorCorrection = this.isViewer2d() ? 1 : 0.75;
        
        // Selection state
        this.hovered = -1;
        this.selected = null;
        this.sceneName = 'envision-labels-3d';
        this.clusterCountLabels = new ClusterCountLabels(this.viewer, this.sceneName);
        this.areas2d = [];
        this.imagesPointsCount = 0;
        
        // Initialize shaders
        this.vertexShader = shaders.vertexShader;
        this.fragmentShader = shaders.fragmentShader; // For 360 images
        this.tagsFragmentShader = shaders.tagsFragmentShader; // Dynamic circle shader

        // Initialize tag handlers
        this.tagHandlers = tagHandlers.createHandlers(this);

        // Load textures (keeping for 360 images only)
        this.textures = textureManager.loadTextures();
        
        // Initialize dynamic icon color schemes
        this.initializeDynamicIconColors();

        // Setup event handlers
        this.setupEventHandlers();
    }
    
    /**
     * Initialize dynamic icon color schemes
     */
    initializeDynamicIconColors() {
        this.iconColors = {
            equipment_2d: { normal: '#00a8b7', hover: '#33c1cf' },        // From TAG_COLOR_HEX
            equipment: { normal: '#00a8b7', hover: '#33c1cf' },           // From TAG_COLOR_HEX
            auto_equipment: { normal: '#00a8b7', hover: '#33c1cf' },      // From TAG_COLOR_HEX

            pipeline: { normal: '#547d9a', hover: '#7da0c2' },            // From TAG_COLOR_HEX
            manual_pipelines: { normal: '#547d9a', hover: '#7da0c2' },    // From TAG_COLOR_HEX

            structural: { normal: '#5f6f64', hover: '#8f9f94' },          // From TAG_COLOR_HEX
            manual_structural: { normal: '#5f6f64', hover: '#8f9f94' },   // From TAG_COLOR_HEX

            inspection: { normal: '#9a5454', hover: '#c27777' },          // From TAG_COLOR_HEX
            amisTag: { normal: '#9a5454', hover: '#c27777' },             // From TAG_COLOR_HEX

            anomaly: { normal: '#FF6347', hover: '#ff9677' },             // From TAG_COLOR_HEX

            'ava-inspect': { normal: '#61b695', hover: '#8fccb3' },       // From TAG_COLOR_HEX (AvaInspect)

            annotation: { normal: '#665b9b', hover: '#948bc3' },          // From TAG_COLOR_HEX (General)
            scaffold: { normal: '#665b9b', hover: '#948bc3' },            // From TAG_COLOR_HEX
            permit: { normal: '#665b9b', hover: '#948bc3' },              // From TAG_COLOR_HEX
            container: { normal: '#665b9b', hover: '#948bc3' },           // From TAG_COLOR_HEX

            // Severity colors (keep existing traffic light system)
            severity: {
                '1': { normal: '#4CAF50', hover: '#81C784' },             // Green - Low
                '2': { normal: '#FF9800', hover: '#FFB74D' },             // Orange - Medium
                '3': { normal: '#F44336', hover: '#E57373' },             // Red - High
                '4': { normal: '#9C27B0', hover: '#BA68C8' },             // Purple - Critical
            }
        };
    }

    /**
     * Set up event listeners for the viewer
     */
    setupEventHandlers() {
        let startX, startY;
        const webGLDOM = this.viewer.canvas.parentElement.parentElement.parentElement;
        this.isMouseDown = false;
        
        // Mouse down event
        this.viewer.canvas.addEventListener("mousedown", (event) => {
            this.isMouseDown = true;
            startX = event.pageX;
            startY = event.pageY;
            store.commit('hideThumbnailPopup', this.viewType);
        });
        
        // Mouse up event
        webGLDOM.addEventListener("mouseup", (event) => {
            this.isMouseDown = false;
            const delta = 6;
            const diffX = Math.abs(event.pageX - startX);
            const diffY = Math.abs(event.pageY - startY);

            if (diffX < delta && diffY < delta) {
                this.onClick(event);
            }
        });
        
        // Mouse move event with throttling for better performance
        let throttleTimeout = null;
        webGLDOM.addEventListener("mousemove", (event) => {
            if (!this.isMouseDown) {
                if (throttleTimeout) return;
                
                throttleTimeout = setTimeout(() => {
                    this.onMouseMove(event);
                    throttleTimeout = null;
                }, 16); // ~60fps
            }
        }, false);
        
        // Mouse wheel event
        const isFirefox = (/Firefox/i.test(navigator.userAgent));
        const mousewheelevt = isFirefox ? "DOMMouseScroll" : "mousewheel";
        this.viewer.canvas.addEventListener(mousewheelevt, (event) => {
            store.commit('hideThumbnailPopup', this.viewType);
        }, false);
    }

    /**
     * Load the extension
     * @returns {boolean} Success status
     */
    async load() {
        // Camera change event handler
        this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, () => {
            if (this.isViewer2d() && !store.state.selectedLevel?.mappingViewersPointsInfo?.isReady()) return false;

            let position = this.viewer.navigation.getPosition();
            if (this.isViewer2d() && !!store.state.selectedLevel?.mappingViewersPointsInfo?.isReady()) {
                position = store.state.selectedLevel.mappingViewersPointsInfo._2dTo3d(position);
            }

            this.pointClouds.forEach(o => this.updatePointCloudCameraPosition(o, position));

            if (this.isViewer2d()) {
                let zChanged = Math.abs(this.viewer.navigation.getPosition().z - this.cameraZ) > 0.0001;

                if (zChanged) {
                    this.cameraZ = this.viewer.navigation.getPosition().z;
                    this.updateTags(10, true);
                    this.on2dZoomChanged();
                }
            }
        });

        // Geometry loaded event handler
        this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, (event) => {
            if (this.isViewer2d() && !store.state.selectedLevel?.mappingViewersPointsInfo?.isReady()) return false;
            this.update360Images();
            this.updateTags(1000, true);
            // 3D only. The 2D plan viewer sets its own white background in
            // forgeviewer.js:197 and then loads this extension two lines later,
            // so an unguarded call here repaints every plan sheet dark teal.
            if (!this.isViewer2d()) {
                applyViewerBackground(
                    this.viewer,
                    store.state.userSettings?.displaySettings?.viewerBackgroundColor,
                );
            }
        });

        await this.trackScreenChanges();
        
        return true;
    }

    handleScreenChange() {
        debugLog('labels3dExt', 'Screen changed!');
        this.update360Images();
        this.updateTags();
    }

    listenOnDevicePixelRatio() {
        const self = this;
        function onChange() {
            debugLog('labels3dExt', `devicePixelRatio changed: ${window.devicePixelRatio}`);
            self.handleScreenChange();
            self.listenOnDevicePixelRatio();
        }
        matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener("change", onChange, { once: true });
    }

    /**
     * Track screen changes using the Window Management API
     * @returns {Promise<void>}
     */
    async trackScreenChanges() {
        debugLog('labels3dExt', 'Initializing screen change tracking');

        // Store bound handler reference for later removal
        this.boundHandleScreenChange = this.handleScreenChange.bind(this);

        try {
            screen.addEventListener('change', this.boundHandleScreenChange);
            document.addEventListener('resize', this.boundHandleScreenChange);
            this.listenOnDevicePixelRatio();
        } catch (err) {
            debugError('labels3dExt', 'Screen change tracking error', err);
            debugWarn('labels3dExt', `Window Management API error: ${err.message}`);
        }
    }

    /**
     * Unload the extension
     * @returns {boolean} Success status
     */
    async unload() {
        this.clusterCountLabels.dispose();
        screen.removeEventListener('change', this.boundHandleScreenChange);
        document.removeEventListener('resize', this.boundHandleScreenChange);
        store.state.forgeService?.restoreViewerCursor(this.viewer);
        return true;
    }

    /**
     * Update point cloud camera position
     * @param {Object} pointCloud - Point cloud object
     * @param {Object} position - Camera position
     */
    updatePointCloudCameraPosition(pointCloud, position) {
        if (!pointCloud) return;

        pointCloud.material.uniforms['cameraX'].value = position.x;
        pointCloud.material.uniforms['cameraY'].value = position.y;
        pointCloud.material.uniforms['cameraZ'].value = position.z;

        if (pointCloud.material.uniforms['is360Open']) {
            pointCloud.material.uniforms['is360Open'].value = store.state.selectedImage ? 1 : 0;
        }

        pointCloud.material.needsUpdate = true;
    }

    /**
     * Update visibility range for point clouds.
     * Delegates per-type range derivation to getEffectiveVisibilityRangeFor so
     * the shader uniform and JS hit-testing share a single source of truth.
     */
    updateVisibilityRange() {
        this.pointClouds.forEach(o => {
            o.material.uniforms['visibilityRange'].value = this.getEffectiveVisibilityRangeFor(o);
            o.material.needsUpdate = true;
        });

        this.viewer.impl.invalidate(false, false, true);
    }

    /**
     * Clear 360 images icons
     */
    clear360ImagesIcons() {
        this.clearPointCloudsWithType('360Cluster');
        this.clearPointCloudsWithType('faro');
        this.clearPointCloudsWithType('leica');
        this.clearPointCloudsWithType('gps');
        this.clearPointCloudsWithType('geoslam');
        this.clusterCountLabels.clear();
    }

    /**
     * Update 360 images after their data may have changed (level, filters, visibility, screen).
     * On the 2D plan this also rebuilds the bubble levels.
     */
    update360Images() {
        this.bubbleLevelsStale = true;
        this.draw360Images();
    }

    /**
     * 2D plan zoom changed. The bubble levels are already built, so the icons are only redrawn
     * when the zoom crosses into another level; otherwise the counts are just kept at their size.
     */
    on2dZoomChanged() {
        const shown = store.state.forgeService.getAllShownPanoramaImagesIn3D();
        if (this.bubbleLevelsStale
            || this.bubbleDrawKey(this.bubbleDataKey(shown)) !== this.bubbleDrawnKey
            || this.current2dBubbleLevel() !== this.bubbleLevel) {
            this.draw360Images();
            return;
        }
        this.clusterCountLabels.rescale();
        this.viewer.impl.invalidate(false, false, true);
    }

    /**
     * Identifies the scans the bubble levels are built from. Scans are shown, hidden, locked and
     * loaded from many places that do not all call update360Images, so the levels are checked
     * against this key instead of trusting those calls (~4 ms for 5k scans).
     * @param {Object[]} shown - getAllShownPanoramaImagesIn3D()
     * @returns {string}
     */
    bubbleDataKey(shown) {
        return `${store.state.selectedLevel?.name}|${shown.length}|${shown.map(img => img.id).join(',')}`;
    }

    /**
     * Identifies what the last 2D draw showed: the scans and whether 360 icons were on.
     * @param {string} dataKey - bubbleDataKey()
     * @returns {string}
     */
    bubbleDrawKey(dataKey) {
        return `${!!store.state.userSettings.displaySettings.show360}|${dataKey}`;
    }

    /**
     * The 360 scans of the selected level on the 2D plan, grouped at the current zoom level.
     * @param {Object[]} shown - getAllShownPanoramaImagesIn3D()
     * @param {string} dataKey - bubbleDataKey(shown)
     * @returns {{ images: Object[], bubbles: Object[][] }}
     */
    get2dBubblesAndImages(shown, dataKey) {
        const level = store.state.selectedLevel;
        if (this.bubbleLevelsStale || dataKey !== this.bubbleLevelsKey) {
            const mapping = level.mappingViewersPointsInfo;
            const images = level.filterImagesInside(shown);
            this.bubbleLevels = buildClusterLevels(images, {
                getPoint: img => mapping._3dTo2d(img.forgeCameraPosition),
                getKey: img => img.id,
                radiusPx: BUBBLE_CLUSTER_RADIUS_PX,
                maxLevel: BUBBLE_MAX_LEVEL,
            });
            this.bubbleLevelsKey = dataKey;
            this.bubbleLevelsStale = false;
        }
        this.bubbleLevel = this.current2dBubbleLevel();
        const { clusters, singles } = clustersAtLevel(this.bubbleLevels, this.bubbleLevel);
        return { images: singles, bubbles: clusters };
    }

    /**
     * @returns {number} Bubble level for the current 2D zoom (BUBBLE_MAX_LEVEL + 1 = no grouping)
     */
    current2dBubbleLevel() {
        if (this.viewer.getCamera().orthoScale < BUBBLE_UNCLUSTER_VIEW_HEIGHT) return BUBBLE_MAX_LEVEL + 1;
        return clusterLevelFor(this.bubbleLevels, viewerPxPerUnit(this.viewer));
    }

    /**
     * Draw the 360 image icons from the current data (on the 2D plan: the current bubble level)
     */
    draw360Images() {
        if (!this.viewer || !this.viewer.impl) return;
        this.clear360ImagesIcons();
        this.bubbleDrawnKey = null;
        if (this.isViewer2d() && (!store.state.selectedLevel || !store.state.selectedLevel.hasAllMappingInfo())) {
            this.viewer.impl.invalidate(true, true, true);
            return;
        }

        // Group once for every scan type (it used to be regrouped per type)
        let bubblesAndImages = null;
        if (this.isViewer2d()) {
            const shown = store.state.forgeService.getAllShownPanoramaImagesIn3D();
            const dataKey = this.bubbleDataKey(shown);
            this.bubbleDrawnKey = this.bubbleDrawKey(dataKey);
            if (store.state.userSettings.displaySettings.show360) {
                bubblesAndImages = this.get2dBubblesAndImages(shown, dataKey);
            }
        }

        this.update360ImagesByType('360Cluster', this.textures['cluster'], this.textures['clusterHover'], bubblesAndImages);
        if (this.isViewer2d())
            this.update360ImagesByType('faro', this.textures['faro2d'], this.textures['faro2dHover'], bubblesAndImages);
        else
            this.update360ImagesByType('faro', this.textures['faro'], this.textures['faroHover']);

        if (this.isViewer2d())
            this.update360ImagesByType('leica', this.textures['leica2d'], this.textures['leica2dHover'], bubblesAndImages);
        else
            this.update360ImagesByType('leica', this.textures['leica'], this.textures['leicaHover']);

        if (this.isViewer2d())
            this.update360ImagesByType('gps', this.textures['gps2d'], this.textures['gps2dHover'], bubblesAndImages);
        else
            this.update360ImagesByType('gps', this.textures['gps'], this.textures['gpsHover']);

        if (this.isViewer2d())
            this.update360ImagesByType('geoslam', this.textures['geoslam2d'], this.textures['geoslam2dHover'], bubblesAndImages);
        else
            this.update360ImagesByType('geoslam', this.textures['geoslam'], this.textures['geoslamHover']);

        if (this.isViewer2d()) this.updateClusterPoly();

        // Tell the ElevationLegend whether at least one individual (non-cluster) 360 scan
        // is visible right now — only the 2D plan viewer's extension instance owns this flag;
        // the 3D viewer instance must NOT touch it (would reset the state each 3D update).
        if (this.isViewer2d()) {
            const individualCount = this.pointClouds.filter(
                pc => pc.type !== '360Cluster' && pc.forgeData && pc.forgeData[0] && !pc.forgeData[0].isCluster
            ).length;
            store.commit('SET_HAS_VISIBLE_INDIVIDUAL_SCANS', individualCount > 0);
        }

        setTimeout(() => {
            if (this.viewer && this.viewer.impl)
                this.viewer.impl.invalidate(true, true, true);
        }, 50);
    }

    /**
     * Update 360 images by type
     * @param {string} type - Image type
     * @param {Object} texture - Texture object
     * @param {Object} hoveringTexture - Hovering texture object
     * @param {{ images: Object[], bubbles: Object[][] }|null} [bubblesAndImages] - 2D plan only: scans grouped at the current level
     */
    update360ImagesByType(type, texture, hoveringTexture, bubblesAndImages = null) {
        let _this = this;
        var data = [];
        if (this.isViewer2d()) {
            //update shown 360 images
            if (store.state.userSettings.displaySettings.show360 && bubblesAndImages) {
                if (type == '360Cluster') {
                    if (bubblesAndImages.bubbles) {
                        bubblesAndImages.bubbles.forEach(list => {
                            var box = new THREE.Box3().setFromPoints(list.map(o => o.forgeCameraPosition));
                            var center = box.getCenter();
                            var pt = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(center);
                            let points = list.map(o => store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(o.forgeCameraPosition));
                            pt.z = .1;
                            data.push({
                                name: list.length,
                                points: points,
                                isCluster: true,
                                count: list.length,
                                point: pt,
                                onClick: () => {
                                    var min = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(box.min);
                                    var max = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(box.max);
                                    var bubbleBox = new THREE.Box3(min, max);
                                    zoomToFitBox2D(_this.viewer, bubbleBox);
                                }
                            });
                        });
                    }
                } else {
                    if (bubblesAndImages.images) {
                        bubblesAndImages.images.filter(o => o.scanType == type).forEach(img => {
                            var pt = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(img.forgeCameraPosition);
                            const level = store.state.selectedLevel;
                            const elevPct = computeElevationPercentage(img.forgeCameraPosition.z, level.bottomElevation, level.topElevation);
                            pt.z = 0.1 + (elevPct * 0.01);
                            data.push({
                                name: img.name,
                                thumbnailURL: img.thumbnailURL,
                                type: img.scanType,
                                point: pt,
                                object: img,
                                onClick: (event) => {
                                    store.state.forgeService.onClickOn3dIcon(img, event, _this.viewType);
                                }
                            });
                        });
                    }
                }
            }
        } else {
            var images = store.state.forgeService.getAllShownPanoramaImagesIn3D().filter(o => o.scanType == type);
            images = images.filter(o => store.state.forgeService.getMainExtension()?.isWithinCropBox(o.getCameraPositionInViewer()));

            images.forEach(img => {
                let point = img.getCameraPositionInViewer();
                data.push({
                    name: img.name,
                    thumbnailURL: img.thumbnailURL,
                    object: img,
                    type: img.scanType,
                    point: point,
                    onClick: (event) => {
                        store.state.forgeService.onClickOn3dIcon(img, event, _this.viewType);
                    }
                });
            });
        }

        if (data.length > 0) {
            let visRange = this.getVisibilityRange();
            const isIndividualScanPass = this.isViewer2d() && type !== '360Cluster';

            data.forEach(o => {
                // Build ring options for individual (non-cluster) scans on the 2D plan:
                // the shader draws a thin colored stroke at the icon edge whose color
                // encodes the scan's elevation within the current level.
                let ringOptions = null;
                if (isIndividualScanPass && !o.isCluster && o.object) {
                    ringOptions = {
                        drawElevationRing: true,
                        elevationRingColor: this.getElevationRingColorForScan(o.object),
                    };
                }

                let images360Geometry = this.getPointCloudGeometryForData([o]);
                let images360PointCloud = this.initMesh_PointCloud(images360Geometry, texture, hoveringTexture, true, false, visRange, ringOptions);
                images360PointCloud.forgeData = [o];
                images360PointCloud.type = type;
                this.pointClouds.push(images360PointCloud);

                if (o.isCluster) {
                    this.clusterCountLabels.add(o.count, new THREE.Vector3(o.point.x, o.point.y, o.point.z + .05));
                }
            });
        }
    }

    /**
     * Compute the elevation ring color for a single 360 scan, mapping its
     * elevation to a position along the blue→cyan→yellow→red gradient within
     * the current level.
     * @param {Object} img - Image360 instance
     * @returns {THREE.Vector3} linear RGB in [0, 1] for the `elevationRingColor` uniform
     */
    getElevationRingColorForScan(img) {
        const level = store.state.selectedLevel;
        if (!level || !img || !img.forgeCameraPosition) {
            return new THREE.Vector3(0.5, 0.5, 0.5);
        }
        const pct = computeElevationPercentage(
            img.forgeCameraPosition.z,
            level.bottomElevation,
            level.topElevation
        );
        const c = interpolateHeatColor(pct);
        return new THREE.Vector3(c.r, c.g, c.b);
    }

    /**
     * Clear 3D tags
     */
    clear3DTags(ignoreHighlight = false) {
        this.clearPointCloudsWithType('equipment_2d');
        this.clearPointCloudsWithType('auto_equipment');
        this.clearPointCloudsWithType('equipment');
        this.clearPointCloudsWithType('anomaly');
        this.clearPointCloudsWithType('ava-inspect');
        this.clearPointCloudsWithType('structural');
        this.clearPointCloudsWithType('manual_structural');
        this.clearPointCloudsWithType('pipeline');
        this.clearPointCloudsWithType('manual_pipelines');
        this.clearPointCloudsWithType('annotation');
        this.clearPointCloudsWithType('inspection');
        this.clearPointCloudsWithType('severity');
        this.clearPointCloudsWithType('amisTag');
        this.clearPointCloudsWithType('scaffold');
        this.clearPointCloudsWithType('permit');
        this.clearPointCloudsWithType('container');

        this.clearPointCloudsWithType('equipment_2dOverlay');
        this.clearPointCloudsWithType('auto_equipmentOverlay');
        this.clearPointCloudsWithType('equipmentOverlay');
        this.clearPointCloudsWithType('anomalyOverlay');
        this.clearPointCloudsWithType('ava-inspectOverlay');
        this.clearPointCloudsWithType('structuralOverlay');
        this.clearPointCloudsWithType('manual_structuralOverlay');
        this.clearPointCloudsWithType('pipelineOverlay');
        this.clearPointCloudsWithType('manual_pipelinesOverlay');
        this.clearPointCloudsWithType('annotationOverlay');
        this.clearPointCloudsWithType('inspectionOverlay');
        this.clearPointCloudsWithType('severityOverlay');
        this.clearPointCloudsWithType('amisTagOverlay');
        this.clearPointCloudsWithType('scaffoldOverlay');
        this.clearPointCloudsWithType('permitOverlay');
        this.clearPointCloudsWithType('containerOverlay');

        if (!ignoreHighlight) this.clearHighlightingTags();

        this.areas2d.forEach(o => disposeArea2d(o, this.viewer, this.sceneName));
        this.areas2d = [];
    }

    /**
     * Clear point clouds with specific type
     * @param {string} type - Point cloud type
     */
    clearPointCloudsWithType(type) {
        for (let i = this.pointClouds.length - 1; i >= 0; i--) {
            if (this.pointClouds[i].type != type)
                continue;
            removeFromScene(this.pointClouds[i], this.viewer, this.sceneName);
            this.pointClouds.splice(i, 1);
        }
    }

    /**
     * Force the next 2D tag pass to re-project every tag. Call it when a tag's
     * geometry changed without changing the tag count.
     */
    invalidateTagGeometryCaches() {
        this.tagHandlers.resetClusterCaches();
    }

    /**
     * Update tags with a delay
     * @param {number} timeout - Delay in milliseconds
     */
    updateTags(timeout = 1000, ignoreHighlight = false) {
        if (this.updateTagsTimeout) {
            clearTimeout(this.updateTagsTimeout);
            this.updateTagsTimeout = null;
        }

        this.updateTagsTimeout = setTimeout(() => {
            this.tagHandlers.clearClusterTextMeshes();
            this.updateTagsHandler(ignoreHighlight);
        }, timeout);
    }

    /**
     * Update tags handler
     */
    updateTagsHandler(ignoreHighlight = false) {
        if (!this.viewer || !this.viewer.impl) return;

        this.areas2d.forEach(o => disposeArea2d(o, this.viewer, this.sceneName));
        this.areas2d = [];
        this.clear3DTags(ignoreHighlight);

        // Make removeFromScene available to handlers
        this.removeFromScene = removeFromScene;

        let hoveringColor = new THREE.Color(this.hoveringColor);
        let hoverColorVector = new THREE.Vector3(hoveringColor.r, hoveringColor.g, hoveringColor.b);

        // Store current tag type before updating
        this.currentTagType = 'equipment_2d';
        this.updateTagsWithType('equipment_2d');

        this.currentTagType = 'anomaly';
        this.updateTagsWithType('anomaly');

        this.currentTagType = 'ava-inspect';
        this.updateTagsWithType('ava-inspect');

        this.currentTagType = 'manual_pipelines';
        this.updateTagsWithType('manual_pipelines');
        
        this.currentTagType = 'pipeline';
        this.updateTagsWithType('pipeline');
        
        this.currentTagType = 'manual_structural';
        this.updateTagsWithType('manual_structural');
        
        this.currentTagType = 'structural';
        this.updateTagsWithType('structural');
        
        this.currentTagType = 'equipment';
        this.updateTagsWithType('equipment');
        
        this.currentTagType = 'auto_equipment';
        this.updateTagsWithType('auto_equipment');
        
        this.currentTagType = 'annotation';
        this.updateTagsWithType('annotation');
        
        this.currentTagType = 'inspection';
        this.updateTagsWithType('inspection');
        
        this.currentTagType = 'severity';
        this.updateTagsWithType('severity');
        
        this.currentTagType = 'amisTag';
        this.updateTagsWithType('amisTag');
        
        this.currentTagType = 'scaffold';
        this.updateTagsWithType('scaffold');
        
        this.currentTagType = 'permit';
        this.updateTagsWithType('permit');
        
        this.currentTagType = 'container';
        this.updateTagsWithType('container');
        
        // Reset current tag type
        this.currentTagType = null;

        setTimeout(() => {
            if (this.viewer && this.viewer.impl)
                this.viewer.impl.invalidate(true, true, true);
        }, 100);
    }

    /**
     * Update tags with specific type using dynamic icons
     * @param {string} type - Tag type
     */
    updateTagsWithType(type) {
        // Get common settings
        const settings = this.getTagTypeSettings();
        const viewerData = this.getViewerPositionData();
        
        // Delegate to appropriate tag handler
        const handler = this.tagHandlers.getHandler(type);
        if (handler) {
            const data = handler.collectData(settings, viewerData);
            this.createDynamicPointCloudsForTagData(data, type, viewerData.visibilityRange);
        }
    }

    /**
     * Get common settings for tag display
     * @returns {Object} Settings object
     */
    getTagTypeSettings() {
        const isGlobalSearchTabShown = store.state.isTabShown && store.state.selectedTab == 0;
        const isGlobalSearchFilter = isGlobalSearchTabShown && store.getters['GET_GLOBAL_SEARCH_TEXT'];
        const displaySettings = store.state.userSettings.displaySettings;
        
        return {
            isGlobalSearchTabShown,
            isGlobalSearchFilter,
            displaySettings
        };
    }

    /**
     * Get current viewer position data
     * @returns {Object} Viewer position data
     */
    getViewerPositionData() {
        let position = this.viewer.navigation.getPosition();
        
        return {
            position,
            visibilityRange: this.getVisibilityRange()
        };
    }

    /**
     * Draw tag area
     * @param {Object} tag - Tag object
     */
    drawTagArea(tag) {
        if (tag.area2dCoordinates && this.isViewer2d()) {
            let area = this.drawTagAreaByColor(tag, null);
            this.areas2d.push(area);
        }
    }

    /**
     * Draw tag area by color
     * @param {Object} tag - Tag object
     * @param {Object} color - Color object
     * @returns {Object} Area object
     */
    drawTagAreaByColor(tag, color) {
        let points = tag.area2dPoints.map(o => store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(o));
        if (!color) {
            color = tag.getLabelColor();
            if (tag.getCategoryColor) {
                let categColor = tag.getCategoryColor();
                if (categColor)
                    color = new THREE.Color(color.r, color.g, color.b).getHex();
            }
        }
        let area = drawArea2d(points, this.viewer, this.sceneName, color);
        area.tempPolyGl.material.depthTest = true;
        area.tempPolyGl.material.depthWrite = true;
        area.tempPolyGl.position.setZ(.5);
        return area;
    }

    /**
     * Get point cloud geometry for data
     * @param {Array} data - Data array
     * @returns {Object} Geometry object
     */
    getPointCloudGeometryForData(data) {
        var position = new Float32Array(data.length * 3);
        var color = new Float32Array(data.length * 3);
        var position3d = new Float32Array(data.length * 3);

        for (let i = 0; i < data.length; i++) {
            const p = data[i];

            // Position
            position[(i * 3)] = p.point.x;
            position[(i * 3) + 1] = p.point.y;
            position[(i * 3) + 2] = p.point.z;

            // Color
            color[(i * 3)] = 0;
            color[(i * 3) + 1] = 0;
            color[(i * 3) + 2] = 0;

            if (this.isViewer2d()) {
                let point = store.state.selectedLevel.mappingViewersPointsInfo._2dTo3d(p.point);
                position3d[(i * 3)] = point.x;
                position3d[(i * 3) + 1] = point.y;
                position3d[(i * 3) + 2] = point.z;
            }
        }

        var geometry = new THREE.BufferGeometry();

        geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(color, 3));
        geometry.setAttribute('position3d', new THREE.BufferAttribute(position3d, 3));

        return geometry;
    }

    /**
     * Get point cloud tag geometry
     * @param {Object} tag - Tag object
     * @param {Object} _color - Color object
     * @returns {Object} Geometry object
     */
    getPointCloudTagGeometry(tag, _color) {
        var position = new Float32Array(3);
        var color = new Float32Array(3);
        var position3d = new Float32Array(3);

        position[0] = tag.point.x;
        position[1] = tag.point.y;
        position[2] = tag.point.z;

        let c = new THREE.Color(_color.r, _color.g, _color.b);

        if (c.r <= 1 && c.g <= 1 && c.b <= 1) {
            c.r = c.r * this.colorCorrection;
            c.g = c.g * this.colorCorrection;
            c.b = c.b * this.colorCorrection;
        }

        color[0] = c.r;
        color[1] = c.g;
        color[2] = c.b;

        if (this.isViewer2d()) {
            let point = store.state.selectedLevel.mappingViewersPointsInfo._2dTo3d(tag.point);
            position3d[0] = point.x;
            position3d[1] = point.y;
            position3d[2] = point.z;
        }

        var geometry = new THREE.BufferGeometry();

        geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(color, 3));
        geometry.setAttribute('position3d', new THREE.BufferAttribute(position3d, 3));

        return geometry;
    }

    /**
     * Initialize point cloud mesh
     * @param {Object} geometry - Geometry object
     * @param {Object} texture - Texture object
     * @param {Object} hoveringTexture - Hovering texture object
     * @param {boolean} depthWrite - Depth write flag
     * @param {boolean} isTag - Is tag flag
     * @param {number} visibilityRange - Visibility range
     * @returns {Object} Point cloud object
     */
    initMesh_PointCloud(geometry, texture, hoveringTexture, depthWrite, isTag, visibilityRange, ringOptions) {
        let position = this.viewer.navigation.getPosition();

        if (this.isViewer2d()) {
            position = store.state.selectedLevel.mappingViewersPointsInfo._2dTo3d(position);
        }

        const drawElevationRing = !!(ringOptions && ringOptions.drawElevationRing);
        const elevationRingColor = (ringOptions && ringOptions.elevationRingColor) || new THREE.Vector3(1, 1, 1);

        var material = new THREE.ShaderMaterial({
            vertexColors: true,
            vertexShader: this.vertexShader,
            fragmentShader: this.fragmentShader,
            depthWrite: depthWrite,
            depthTest: depthWrite,
            uniforms: {
                isTag: { type: "i", value: isTag ? 1 : 0 },
                isViewer2d: { type: "i", value: this.isViewer2d() ? 1 : 0 },
                tex: { type: "t", value: texture },
                hoveringTexture: { type: "t", value: hoveringTexture },
                visibilityRange: { type: "f", value: visibilityRange },
                cameraX: { type: "f", value: position.x },
                cameraY: { type: "f", value: position.y },
                cameraZ: { type: "f", value: position.z },
                scaleFactor: { type: "f", value: this.isViewer2d() ? 1.0 : 1 / store.state.unitScale },
                devicePixelRatio: { type: "f", value: window.devicePixelRatio || 1.0 },
                is360Open: { type: "i", value: store.state.selectedImage ? 1 : 0 },
                drawElevationRing: { type: "i", value: drawElevationRing ? 1 : 0 },
                elevationRingColor: { type: "v3", value: elevationRingColor },
            }
        });

        var pointCloud = new THREE.PointCloud(geometry, material);

        addToScene(pointCloud, this.viewer, this.sceneName);
        return pointCloud;
    }

    /**
     * Initialize point cloud tag mesh with dynamic circle icons
     * @param {Object} geometry - Geometry object
     * @param {Object} texture - Texture object
     * @param {Object} hoveringTexture - Hovering texture object
     * @param {boolean} depthWrite - Depth write flag
     * @param {number} visibilityRange - Visibility range
     * @param {boolean} isCluster - Is cluster flag
     * @param {boolean} isLarge - Is large flag
     * @param {Object} iconOptions - Icon customization options
     * @returns {Object} Point cloud object
     */
    initMesh_PointCloud_Tag(geometry, texture, hoveringTexture, depthWrite, visibilityRange, isCluster, isLarge, iconOptions = null) {
        let position = this.viewer.navigation.getPosition();

        if (this.isViewer2d()) {
            position = store.state.selectedLevel.mappingViewersPointsInfo._2dTo3d(position);
        }

        // Always use dynamic shader icons now
        const fragmentShader = this.tagsFragmentShader;

        // Set up uniforms - always include dynamic icon uniforms
        let uniforms = {
            isTag: { type: "i", value: 1 },
            isLarge: { type: "i", value: isLarge ? 1 : 0 },
            isViewer2d: { type: "i", value: this.isViewer2d() ? 1 : 0 },
            isMISTag: { type: "i", value: 1 },
            tex: { type: "t", value: null }, // Not used anymore
            hoveringTexture: { type: "t", value: null }, // Not used anymore
            useShaderIcon: { type: "i", value: 1 },
            iconColor: { type: "v3", value: iconOptions?.iconColor || new THREE.Vector3(0.2, 0.6, 0.8) },
            hoverColor: { type: "v3", value: iconOptions?.hoverColor || new THREE.Vector3(0.8, 0.4, 0.2) },
            ringWidth: { type: "f", value: iconOptions?.ringWidth || 0.1 },
            flickerOpacity: { type: "f", value: 1.0 },
            flickerState: { type: "i", value: 0 },
            visibilityRange: { type: "f", value: visibilityRange },
            cameraX: { type: "f", value: position.x },
            cameraY: { type: "f", value: position.y },
            cameraZ: { type: "f", value: position.z },
            scaleFactor: { type: "f", value: this.isViewer2d() ? 1.0 : 1 / store.state.unitScale },
            isCluster: { type: "i", value: isCluster ? 1 : 0 },
            isHighlighted: { type: "i", value: 0 },
            devicePixelRatio: { type: "f", value: window.devicePixelRatio || 1.0 },
            is360Open: { type: "i", value: store.state.selectedImage ? 1 : 0 },
        };

        var material = new THREE.ShaderMaterial({
            vertexColors: true,
            vertexShader: this.vertexShader,
            fragmentShader: fragmentShader,
            depthWrite: depthWrite,
            depthTest: depthWrite,
            uniforms: uniforms
        });

        var pointCloud = new THREE.PointCloud(geometry, material);

        if (this.isViewer2d()) pointCloud.position.setZ(1);

        addToScene(pointCloud, this.viewer, this.sceneName);

        return pointCloud;
    }

    /**
     * Handle mouse move event
     * @param {Object} event - Event object
     */
    onMouseMove(event) {
        if (!this.viewer || !this.viewer.model) return;
        if (this.isViewer2d()) disposeArea2d(this.hoveringArea, this.viewer, this.sceneName);

        if (this.isViewer2d() && store.state.forgeService.getDrawPointsExtension()?.measuringAreaOn) return;

        this.pointClouds.forEach(o => {
            let colorAttribute = o.geometry.attributes.color;
            let length = colorAttribute.count;

            for (let i = 0; i < length; i++) {
                let x = i * colorAttribute.itemSize;

                if (o.isTag) {
                    let categoryColor = new THREE.Color(3.0, 3.0, 3.0);

                    if (o.forgeData[i].object instanceof ForgeInspection && o.forgeData[i].object.isFetched) {
                        let color = o.forgeData[i].object.pinColor;
                        categoryColor = new THREE.Color(color);
                    } else if (o.forgeData[i].object?.category > 0) {
                        let color = o.forgeData[i].object.getCategoryColor();

                        if (color) {
                            categoryColor = new THREE.Color(color.r, color.g, color.b);
                        }
                    }

                    colorAttribute.array[x] = categoryColor.r * this.colorCorrection;
                    colorAttribute.array[x + 1] = categoryColor.g * this.colorCorrection;
                    colorAttribute.array[x + 2] = categoryColor.b * this.colorCorrection;
                } else {
                    if (colorAttribute.array[x] > 4 && colorAttribute.array[x + 1] > 4 && colorAttribute.array[x + 2] > 4) {
                        colorAttribute.array[x] = 0.0;
                        colorAttribute.array[x + 1] = 0.0;
                        colorAttribute.array[x + 2] = 0.0;
                    }
                }
            }

            o.geometry.attributes.color.needsUpdate = true;
        });

        this.viewer.impl.invalidate(false, false, true);

        this.hoveringPointCloud = null;
        let raycaster = pointerToRaycaster(this.viewer, event);
        this.updateHitTestForPointCloud(raycaster, this.pointClouds, event);

        if (this.isViewer2d()) this.updateClusterPoly();
    }

    /**
     * Update cluster polygon
     */
    updateClusterPoly() {
        let needsUpdate = false;
        if (this.clusterHoveringAreaMesh) {
            removeFromScene(this.clusterHoveringAreaMesh, this.viewer, this.sceneName);
            this.clusterHoveringAreaMesh = null;
            needsUpdate = true;
        }

        if (this.clusterPolyBorderGl?.length) {
            removeFromScene(this.clusterPolyBorderGl, this.viewer, this.sceneName);
            this.clusterPolyBorderGl = [];
            needsUpdate = true;
        }


        if (this.hoveringPointCloud &&( this.hoveringPointCloud.type == '360Cluster' || this.hoveringPointCloud.isCluster)) {
            var points = this.hoveringPointCloud.forgeData[this.hovered].points;

            if (!points || points.length == 0) return;

            let poly = convexHull(points) ?? points;

            if (!poly) return;

            if (poly) {
                this.clusterPolyBorderGl = [];
                for (let i = 0; i < poly.length - 1; i++) {
                    let lineGl = drawLine(poly[i], poly[i + 1], 0x0000ff, this.viewer, this.sceneName, 2);
                    this.clusterPolyBorderGl.push(lineGl);
                }
                if (poly.length > 2)
                    this.clusterHoveringAreaMesh = drawPolygon(poly, 0x808080, this.viewer, this.sceneName);
                needsUpdate = true;
            }
        }

        if (needsUpdate) this.viewer.impl.invalidate(false, false, true);
    }

    /**
     * Check if viewer is 2D
     * @returns {boolean} Is viewer 2D
     */
    isViewer2d() {
        return this.viewer == store.state.planViewer;
    }

    /**
     * Handle click event
     * @param {Object} event - Event object
     */
    onClick(event) {
        let clickHovered = this.hovered;
        let clickPointCloud = this.hoveringPointCloud;

        // Fast-path stale-hover guard: hover index may have been set before the
        // camera moved (keyboard/orbit/programmatic view change without a
        // mousemove). If the hovered icon is now outside its render range, drop
        // the hover and fall through to the screen-space fallback — which will
        // either find an in-range icon nearby, or legitimately return no hit.
        if (clickHovered !== -1 && clickPointCloud) {
            const pt = clickPointCloud.forgeData[clickHovered]?.point;
            if (pt && !this.isWithinVisibilityRange(clickPointCloud, pt)) {
                clickHovered = -1;
                clickPointCloud = null;
            }
        }

        // Screen-space fallback when hovered is stale due to mousemove throttle
        if (clickHovered == -1) {
            debugLog('labels3dExt', 'onClick fallback engaged; visibility range filter active');
            let clickPos = new THREE.Vector2(event.offsetX, event.offsetY);
            let spriteRadius = 20;
            const camPos = this.viewer.navigation.getPosition();

            for (let pc of this.pointClouds) {
                if (pc.isTag) continue;
                for (let j = 0; j < pc.forgeData.length; j++) {
                    const pt = pc.forgeData[j].point;
                    // Mirror the shader's render cull (shaders.js:31) — geometry
                    // keeps the position but the sprite is invisible, so JS must
                    // skip it too or clicks on empty space fire phantom popups.
                    if (!this.isWithinVisibilityRange(pc, pt, camPos)) continue;
                    let screenPos = this.viewer.worldToClient(pt);
                    if (screenPos && clickPos.distanceTo(screenPos) < spriteRadius) {
                        clickHovered = j;
                        clickPointCloud = pc;
                        break;
                    }
                }
                if (clickHovered !== -1) break;
            }
        }

        if (clickHovered == -1) return;

        this.selected = clickHovered;
        setTimeout(() => {
            clickPointCloud?.forgeData[this.selected]?.onClick(event);
            this.viewer.clearSelection();
        }, 50);
    }

    /**
     * Update hit test for point cloud
     * @param {Object} raycaster - Raycaster object
     * @param {Array} pointClouds - Point clouds array
     * @param {Object} event - Event object
     */
    updateHitTestForPointCloud(raycaster, pointClouds, event) {
        const camPos = this.viewer.navigation.getPosition();
        var nodes = raycaster.intersectObjects(pointClouds);
        nodes = nodes.filter(o =>
            o.distance > 0 &&
            this.isWithinTolerance(o.point, o.object.forgeData[o.index].point, 30) &&
            // Pass the sprite's stored position (what the shader culls against),
            // not o.point which is ray.closestPointToPoint(position) and can land
            // inside the range while the sprite itself is outside — a boundary-
            // case regression of the original phantom-click bug.
            this.isWithinVisibilityRange(o.object, o.object.forgeData[o.index].point, camPos)
        );

        const tolerance3dTags = 0.005 / store.state.unitScale;
        const tolerance360 = 0.1 / store.state.unitScale;

        const getTolerance = (pc) => {
            if (this.isViewer2d()) return 0.004;
            if (pc.isTag) return tolerance3dTags;
            return tolerance360;
        }

        if (nodes.length == 0) {
            for (let i = 0; i < pointClouds.length; i++) {
                for (let j = 0; j < pointClouds[i].forgeData.length; j++) {
                    let pt = pointClouds[i].forgeData[j].point;

                    // Mirror the shader's render cull (shaders.js:31) so hover
                    // cannot land on an invisible sprite.
                    if (!this.isWithinVisibilityRange(pointClouds[i], pt, camPos)) continue;

                    if (!this.isViewer2d() && store.state.areViewsLocked && pointClouds[i].forgeData[j].object?.name === store.state.selectedImage?.name) continue;

                    let distance = raycaster.ray.distanceToPoint(pt);
                    let tole = this.isViewer2d() ? getTolerance(pointClouds[i]) : getTolerance(pointClouds[i]) + this.viewer.navigation.getPosition().distanceTo(pt) * 0.007;
                    if (tole > distance) {
                        nodes.push({
                            object: pointClouds[i],
                            point: pointClouds[i].forgeData[j].point,
                            index: j,
                            distanceToRay: distance
                        });
                    }
                }
            }
        }

        if (!this.isViewer2d()) {
            var hitTest = this.viewer.clientToWorld(event.offsetX, event.offsetY, false);
            if (hitTest && hitTest.point) {
                let tolerance = 16 / store.state.unitScale;
                for (let i = nodes.length - 1; i >= 0; i--) {
                    if (nodes[i].object.type.includes('Overlay') || nodes[i].object.type.includes('HighlightedPointCloud')) {
                        if (nodes[i].point.distanceTo(camPos) > tolerance)
                            nodes.splice(i, 1);
                    } else {
                        // Screen-space priority — keep sprite if cursor is visually over it
                        let spriteScreenPos = this.viewer.worldToClient(nodes[i].point);
                        if (spriteScreenPos) {
                            let cursorPos = new THREE.Vector2(event.offsetX, event.offsetY);
                            let screenDist = cursorPos.distanceTo(spriteScreenPos);

                            if (screenDist > 20 &&
                                nodes[i].point.distanceTo(camPos) > hitTest.point.distanceTo(camPos)) {
                                nodes.splice(i, 1);
                            }
                        } else {
                            // Point behind camera — fall back to original depth check
                            if (nodes[i].point.distanceTo(camPos) > hitTest.point.distanceTo(camPos))
                                nodes.splice(i, 1);
                        }
                    }
                }
            }
        }

        if (nodes.length > 0) {
            nodes.sort(function (a, b) {
                return a.distanceToRay - b.distanceToRay;
            });

            let nearestTag = nodes.find(n => n.object.type == "HighlightedPointCloud" && !!n.object.forgeData?.length) || nodes[0];

            this.hoveringPointCloud = nearestTag.object;
            this.hovered = nearestTag.index;
            nearestTag.object.geometry.attributes.color.array[(this.hovered * 3) + 0] = 5.0;
            nearestTag.object.geometry.attributes.color.array[(this.hovered * 3) + 1] = 5.0;
            nearestTag.object.geometry.attributes.color.array[(this.hovered * 3) + 2] = 5.0;
            nearestTag.object.geometry.attributes.color.needsUpdate = true;
            if (this.isViewer2d() && nearestTag.object.forgeData[this.hovered].object
                && nearestTag.object.forgeData[this.hovered].object.area2dCoordinates)
                this.hoveringArea = this.drawTagAreaByColor(nearestTag.object.forgeData[this.hovered].object, this.hoveringColor);

            this.viewer.impl.invalidate(false, false, true);
            this.viewer.canvas.style.cursor = 'pointer';
        } else {
            this.hovered = -1;
            store.state.forgeService?.restoreViewerCursor(this.viewer);
        }
    }

    /**
     * Check if point is within tolerance
     * @param {Object} point - Point object
     * @param {Object} target - Target object
     * @param {number} tolerancePixels - Tolerance in pixels
     * @returns {boolean} Is within tolerance
     */
    isWithinTolerance(point, target, tolerancePixels) {
        var clientImgPoint = this.viewer.worldToClient(point);
        var clientPointToRay = this.viewer.worldToClient(target);
        if (!clientImgPoint || !clientPointToRay) return false;
        return clientImgPoint.distanceTo(clientPointToRay) < tolerancePixels;
    }

    /**
     * The effective visibility range for a given pointcloud, in viewer units.
     * Encapsulates the per-type overrides that seed the shader `visibilityRange`
     * uniform, so shader rendering and JS hit-testing stay in sync through a
     * single source of truth.
     *
     *   - 2D viewer             → Infinity (feature disabled; shader's 2D path
     *                             uses a separate position3d attribute JS can't
     *                             replicate here)
     *   - HighlightedPointCloud → Infinity (always visible regardless of slider)
     *   - tagsOverlay           → min(slider, 16m / unitScale) — tighter cap
     *   - other pointclouds     → slider value in viewer units
     *
     * @param {Object} pc - Point cloud (THREE.PointCloud)
     * @returns {number} Effective range in viewer units; Infinity means never cull.
     */
    getEffectiveVisibilityRangeFor(pc) {
        if (this.isViewer2d()) return Infinity;
        if (pc?.type === 'HighlightedPointCloud') return Infinity;
        const visRange = this.getVisibilityRange();
        if (pc?.type === 'tagsOverlay') {
            return Math.min(visRange, 16 / store.state.unitScale);
        }
        return visRange;
    }

    /**
     * Mirrors the shader's visibility cull (shaders.js:31) in JavaScript so
     * hit-testing (click + hover) agrees with rendering. Derives the range from
     * getEffectiveVisibilityRangeFor — the same source that seeds the shader
     * uniform — so there is exactly one place that knows the per-type rules.
     *
     * Units: both sides of the comparison are in viewer units. The method
     * returns a value produced by getVisibilityRange (which divides meters by
     * store.state.unitScale), and point.distanceTo(cam) is computed in the
     * same coordinate system the shader uses for `distance`.
     *
     * Non-finite range (Infinity for 2D / HighlightedPointCloud) means "never
     * cull", matching shader semantics where `Infinity < anything` is false.
     *
     * @param {Object} pc       - Point cloud (THREE.PointCloud)
     * @param {Object} point    - World-space THREE.Vector3
     * @param {Object} [camPos] - Optional pre-computed camera position. Pass
     *   this when calling inside a loop to avoid allocating a new Vector3 per
     *   iteration.
     * @returns {boolean} True if `point` is within `pc`'s rendered range.
     */
    isWithinVisibilityRange(pc, point, camPos) {
        const range = this.getEffectiveVisibilityRangeFor(pc);
        if (!Number.isFinite(range)) return true;
        const cam = camPos || this.viewer.navigation.getPosition();
        return point.distanceTo(cam) <= range;
    }

    /**
     * Get visibility range
     * @returns {number} Visibility range
     */
    getVisibilityRange() {
        return this.isViewer2d() ? 1000000 : parseFloat(store.state.userSettings.displaySettings.getVisibilityRangeInViewer());
    }

    /**
     * Clear highlighting tags
     */
    clearHighlightingTags() {
        if (!this.viewer || !this.viewer.impl) return;

        let oldPcs = this.viewer.impl?.overlayScenes[this.sceneName]?.scene?.children?.filter(t => t.type == 'HighlightedPointCloud') ?? [];
        if (oldPcs.length) {
            clearInterval(this.selectedPcInterval);
            this.selectedPcInterval = null;
            removeFromScene(oldPcs, this.viewer, this.sceneName);
            this.pointClouds = this.pointClouds.filter(pc => pc.type !== 'HighlightedPointCloud');
            this.viewer.impl.invalidate(false, false, true);
        }
    }

    /**
     * Create highlighting tags
     * @param {Array} data - Data array
     */
    createHighlightingTags(data) {
        if (!data.filter(t => !!t.point || !!t.position).length) return;

        debugLog('labels3dExt', `Create highlight tag for ${data.length} items`);

        this.clearHighlightingTags();

        let highlight = () => {
            if (this.selectedPcInterval) clearInterval(this.selectedPcInterval);

            this.selectedPcInterval = setInterval(() => {
                if (!this.viewer.impl || !this.viewer.impl.overlayScenes) return;

                let points = this.viewer.impl.overlayScenes[this.sceneName].scene.children.filter(t => t.type == 'HighlightedPointCloud');

                points.forEach(o => {
                    if (!o || !o.material || !o.material.uniforms || !o.material.uniforms.flickerState) return;

                    // Flicker effect by alternating between normal and hover state
                    // flickerState 0 = normal circle, flickerState 1 = hover state with rings
                    const currentState = o.material.uniforms.flickerState.value;
                    const newState = currentState === 0 ? 1 : 0;

                    o.material.uniforms.flickerState.value = newState;
                    o.material.needsUpdate = true;
                    this.viewer.impl.invalidate(false, false, true);
                });

            }, 500);
        };

        let length = data.length;

        if (length === 1 && this.isViewer2d()) {
            let item = data[0];
            store.state.forgeService.onClickOn3dIcon(item, null, this.viewType);
        }

        for (let i = 0; i < length; i++) {
            const tag = data[i];
            const p = tag.point ?? tag.position;

            if (p) {
                var geometry = new THREE.BufferGeometry();
                var position = new Float32Array(3);
                var color = new Float32Array(3);
                var position3d = new Float32Array(3);

                let forgeData = [];

                let point = this.isViewer2d() ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(p) : tag.getPointInView();

                if (this.isViewer2d() && tag instanceof ForgeEquipment) {
                    point.x += .001;
                }

                position[0] = point.x;
                position[1] = point.y;
                position[2] = point.z;

                color[0] = 3.0;
                color[1] = 3.0;
                color[2] = 3.0;

                forgeData.push({
                    name: tag.name,
                    thumbnailURL: tag.thumbnailUrl,
                    object: tag,
                    point: point,
                    onClick: (event) => {
                        if (tag instanceof ForgeInspection) {
                            store.dispatch('FETCH_INSPECTION_TAG_BY_ID', tag.id).then(inspection => {
                                store.state.forgeService.onClickOn3dIcon(inspection, event, this.viewType);
                            });
                        } else if (tag instanceof ForgeAvaInspect) {
                            store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', tag.id).then(inspect => {
                                store.state.forgeService.onClickOn3dIcon(inspect, event, this.viewType);
                            });
                        } else if (tag instanceof ForgeEquipment) {
                            store.dispatch('FETCH_EQUIPMENT_TAG_BY_ID', tag.id).then(equipment => {
                                store.state.forgeService.onClickOn3dIcon(equipment, event, this.viewType);
                            });
                        } else if (tag instanceof ForgeScaffold) {
                            store.dispatch('FETCH_SCAFFOLD_TAG_BY_ID', tag.id).then(scaffold => {
                                store.state.forgeService.onClickOn3dIcon(scaffold, event, this.viewType);
                            });
                        } else if (tag instanceof ForgePipeline) {
                            store.dispatch('FETCH_PIPELINE_TAG_BY_ID', tag.id).then(pipeline => {
                                store.state.forgeService.onClickOn3dIcon(pipeline, event, this.viewType);
                            });
                        } else if (tag instanceof ForgeStructural) {
                            store.dispatch('FETCH_STRUCTURAL_TAG_BY_ID', tag.id).then(structural => {
                                store.state.forgeService.onClickOn3dIcon(structural, event, this.viewType);
                            });
                        } else if (tag instanceof ForgeAnnotation) {
                            store.dispatch('FETCH_GENERAL_TAG_BY_ID', tag.id).then(annotation => {
                                store.state.forgeService.onClickOn3dIcon(annotation, event, this.viewType);
                            });
                        } else {
                            store.state.forgeService.onClickOn3dIcon(tag, event, this.viewType);
                        }
                    }
                });

                if (this.isViewer2d()) point = store.state.selectedLevel.mappingViewersPointsInfo._2dTo3d(point);

                position3d[(i * 3) + 0] = point.x;
                position3d[(i * 3) + 1] = point.y;
                position3d[(i * 3) + 2] = point.z;

                geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
                geometry.setAttribute('color', new THREE.BufferAttribute(color, 3));
                geometry.setAttribute('position3d', new THREE.BufferAttribute(position3d, 3));

                let cameraPosition = this.viewer.navigation.getPosition();

                // Determine tag type for color selection
                let tagType = 'annotation'; // default
                if (tag instanceof ForgeEquipment) tagType = 'equipment';
                else if (tag instanceof ForgePipeline) tagType = 'pipeline';
                else if (tag instanceof ForgeStructural) tagType = 'structural';
                else if (tag instanceof ForgeInspection) tagType = 'inspection';
                else if (tag instanceof ForgeScaffold) tagType = 'scaffold';
                else if (tag instanceof ForgeAvaInspect) tagType = 'ava-inspect';

                // Get dynamic colors for highlighting - use original tag colors
                debugLog('labels3dExt', 'Tag pin color', tag.pinColor);
                const { iconColor, hoverColor } = this.getDynamicTagColors('', tagType, tag.pinColor);

                // Create dynamic icon options using original colors (no color change)
                const iconOptions = this.createDynamicIconOptions(iconColor, hoverColor);

                var material = new THREE.ShaderMaterial({
                    vertexColors: true,
                    vertexShader: this.vertexShader,
                    fragmentShader: this.tagsFragmentShader,
                    depthWrite: false,
                    depthTest: false,
                    uniforms: {
                        isTag: { type: "i", value: 1 },
                        isViewer2d: { type: "i", value: this.isViewer2d() ? 1 : 0 },
                        isMISTag: { type: "i", value: 0 },
                        useShaderIcon: { type: "i", value: 1 },
                        iconColor: { type: "v3", value: iconOptions.iconColor },
                        hoverColor: { type: "v3", value: iconOptions.hoverColor },
                        ringWidth: { type: "f", value: iconOptions.ringWidth },
                        flickerOpacity: { type: "f", value: 1.0 },
                        flickerState: { type: "i", value: 0 },
                        tex: { type: "t", value: null },
                        hoveringTexture: { type: "t", value: null },
                        visibilityRange: { type: "f", value: Infinity },
                        cameraX: { type: "f", value: cameraPosition.x },
                        cameraY: { type: "f", value: cameraPosition.y },
                        cameraZ: { type: "f", value: cameraPosition.z },
                        scaleFactor: { type: "f", value: this.isViewer2d() ? 1.0 : 1 / store.state.unitScale },
                        isCluster: { type: "i", value: 0 },
                        isHighlighted: { type: "i", value: 1 },
                        devicePixelRatio: { type: "f", value: window.devicePixelRatio || 1.0 },
                    }
                });

                let pointCloud = new THREE.PointCloud(geometry, material);

                if (this.isViewer2d()) {
                    pointCloud.position.setZ(1);
                }

                pointCloud.type = 'HighlightedPointCloud';

                pointCloud.renderOrder = 999999;
                material.polygonOffset = true;
                material.polygonOffsetFactor = -10;
                material.polygonOffsetUnits = -10;
                material.transparent = true;
                material.opacity = 1.0;
                material.depthTest = false;
                material.needsUpdate = true;
                
                if (forgeData.length) {
                    pointCloud.forgeData = forgeData;
                    pointCloud.isTag = true;
                    pointCloud.isDynamicIcon = true;
                    this.pointClouds.push(pointCloud);
                }

                addToScene(pointCloud, this.viewer, this.sceneName);
            }
        }

        setTimeout(() => {
            if (this.viewer && this.viewer.impl) {
                highlight();
                this.viewer.impl.invalidate(false, true, true);
            }
        }, 100);
    }

    /**
     * Create dynamic point clouds for collected tag data
     * @param {Object} data - Collected tag data
     * @param {string} type - Tag type
     * @param {number} visibilityRange - Visibility range
     */
    createDynamicPointCloudsForTagData(data, type, visibilityRange) {
        for (const [key, value] of Object.entries(data)) {
            if (value.length === 0) continue;

            let isCluster = key.endsWith('-cluster');
            const { iconColor, hoverColor } = this.getDynamicTagColors(key, type);

            // Create point cloud for each tag
            value.forEach(tag => {
                if (!tag.point) return;

                // Create dynamic icon options
                const iconOptions = this.createDynamicIconOptions(iconColor, hoverColor);

                // Create geometry for tag
                const geometry = this.getPointCloudTagGeometry(tag, new THREE.Color(3.0, 3.0, 3.0));

                // Create main point cloud with dynamic icon
                const pc = this.initMesh_PointCloud_Tag(
                    geometry, 
                    null, // No texture needed
                    null, // No hover texture needed
                    true, 
                    visibilityRange, 
                    isCluster,
                    type === 'severity',
                    iconOptions
                );
                pc.forgeData = [tag];
                pc.type = type;
                pc.isTag = true;
                pc.isLarge = type === 'severity';
                pc.isCluster = isCluster;
                pc.isDynamicIcon = true;
                this.pointClouds.push(pc);

                // Create cluster text if needed
                if (tag.clusterCreate) {
                    tag.clusterCreate();
                }

                // Create overlay point cloud for 3D view
                if (!this.isViewer2d()) {
                    const overlayPc = this.initMesh_PointCloud_Tag(
                        geometry, 
                        null,
                        null, 
                        false, 
                        16 / store.state.unitScale,
                        isCluster,
                        false,
                        iconOptions
                    );
                    overlayPc.forgeData = [tag];
                    overlayPc.type = type + 'Overlay';
                    overlayPc.isTag = true;
                    overlayPc.isDynamicIcon = true;
                    this.pointClouds.push(overlayPc);
                }
            });
        }
    }

    /**
     * Get dynamic tag colors based on key and type
     * @param {string} key - Tag key/category
     * @param {string} type - Tag type
     * @returns {Object} Color settings
     */
    getDynamicTagColors(key, type, defaultColor = null) {
        // Remove cluster suffix for color lookup
        let colorKey = key;
        if (key.endsWith('-cluster')) {
            colorKey = key.split('-')[0];
        }

        // Handle severity colors
        if (type === 'severity') {
            const severityColors = this.iconColors.severity[colorKey];
            if (severityColors) {
                return {
                    iconColor: severityColors.normal,
                    hoverColor: severityColors.hover
                };
            }
            // Default severity color if not found
            return {
                iconColor: this.iconColors.severity['2'].normal,
                hoverColor: this.iconColors.severity['2'].hover
            };
        }

        // Handle AMIS tags with custom colors
        if (type === 'amisTag') {
            // Use the key as a color if it's a valid color
            try {
                new THREE.Color(colorKey);
                return {
                    iconColor: colorKey,
                    hoverColor: this.lightenColor(colorKey)
                };
            } catch {
                // Fall back to inspection colors
                return {
                    iconColor: this.iconColors.amisTag.normal,
                    hoverColor: this.iconColors.amisTag.hover
                };
            }
        }

        // Handle inspection tags with custom colors
        if (type === 'inspection') {
            if (store.getters.GET_SELECTED_COLORS_OPTIONS?.length) {
                let color = colorKey || defaultColor;

                if (color) {
                    try {
                        new THREE.Color(color);
                        return {
                            iconColor: color,
                            hoverColor: this.lightenColor(color)
                        };
                    } catch {
                        // Invalid color, use default
                    }
                }
            }
            return {
                iconColor: this.iconColors.inspection.normal,
                hoverColor: this.iconColors.inspection.hover
            };
        }

        // Handle anomaly colors
        if (type === 'anomaly') {
            try {
                new THREE.Color(colorKey);
                return {
                    iconColor: colorKey,
                    hoverColor: this.lightenColor(colorKey)
                };
            } catch {
                return {
                    iconColor: this.iconColors.anomaly.normal,
                    hoverColor: this.iconColors.anomaly.hover
                };
            }
        }

        // Handle standard tag types
        if (this.iconColors[type]) {
            return {
                iconColor: this.iconColors[type].normal,
                hoverColor: this.iconColors[type].hover
            };
        }

        // Handle category colors from forge service
        if (store.state.forgeService && store.state.forgeService.getCategoryColor) {
            try {
                let categoryColor = store.state.forgeService.getCategoryColor(colorKey);
                if (categoryColor) {
                    const hexColor = `#${categoryColor.r.toString(16).padStart(2, '0')}${categoryColor.g.toString(16).padStart(2, '0')}${categoryColor.b.toString(16).padStart(2, '0')}`;
                    return {
                        iconColor: hexColor,
                        hoverColor: this.lightenColor(hexColor)
                    };
                }
            } catch (error) {
                debugWarn('labels3dExt', 'Error getting category color', error);
            }
        }

        // Default fallback colors
        return {
            iconColor: this.iconColors.annotation.normal,
            hoverColor: this.iconColors.annotation.hover
        };
    }

    /**
     * Create a lighter version of a color for hover effects
     * @param {string} color - Hex color string
     * @returns {string} Lightened hex color
     */
    lightenColor(color) {
        try {
            const threeColor = new THREE.Color(color);
            // Increase brightness by 30%
            threeColor.r = Math.min(1.0, threeColor.r + 0.3);
            threeColor.g = Math.min(1.0, threeColor.g + 0.3);
            threeColor.b = Math.min(1.0, threeColor.b + 0.3);
            return `#${threeColor.getHexString()}`;
        } catch {
            return '#64B5F6'; // Default light blue
        }
    }



    /**
     * Create dynamic circle icon options from color
     * @param {string|THREE.Color} color - Icon color (hex string or THREE.Color)
     * @param {string|THREE.Color} hoverColor - Hover color (optional)
     * @param {number} ringWidth - Ring width for hover effect (0.05-0.2)
     * @returns {Object} Icon options for dynamic shader
     */
    createDynamicIconOptions(color, hoverColor = null, ringWidth = 0.1) {
        let iconColor, hoverColorVec;

        // Convert color to THREE.Vector3
        if (typeof color === 'string') {
            const threeColor = new THREE.Color(color);
            iconColor = new THREE.Vector3(threeColor.r, threeColor.g, threeColor.b);
        } else if (color instanceof THREE.Color) {
            iconColor = new THREE.Vector3(color.r, color.g, color.b);
        } else {
            iconColor = new THREE.Vector3(0.2, 0.6, 0.8); // Default blue
        }

        // Set hover color (default to lighter version of base color)
        if (hoverColor) {
            if (typeof hoverColor === 'string') {
                const threeHoverColor = new THREE.Color(hoverColor);
                hoverColorVec = new THREE.Vector3(threeHoverColor.r, threeHoverColor.g, threeHoverColor.b);
            } else if (hoverColor instanceof THREE.Color) {
                hoverColorVec = new THREE.Vector3(hoverColor.r, hoverColor.g, hoverColor.b);
            }
        } else {
            // Create a lighter version of the base color for hover
            hoverColorVec = new THREE.Vector3(
                Math.min(1.0, iconColor.x + 0.3),
                Math.min(1.0, iconColor.y + 0.3),
                Math.min(1.0, iconColor.z + 0.3)
            );
        }

        return {
            useDynamicIcon: true,
            iconColor: iconColor,
            hoverColor: hoverColorVec,
            ringWidth: Math.max(0.05, Math.min(0.2, ringWidth))
        };
    }

    /**
     * Update dynamic icon color for existing point cloud
     * @param {Object} pointCloud - Point cloud object
     * @param {string|THREE.Color} newColor - New icon color
     * @param {string|THREE.Color} newHoverColor - New hover color (optional)
     */
    updateDynamicIconColor(pointCloud, newColor, newHoverColor = null) {
        if (!pointCloud || !pointCloud.material || !pointCloud.material.uniforms.useShaderIcon) {
            return;
        }

        const iconOptions = this.createDynamicIconOptions(newColor, newHoverColor);

        pointCloud.material.uniforms.iconColor.value = iconOptions.iconColor;
        pointCloud.material.uniforms.hoverColor.value = iconOptions.hoverColor;
        pointCloud.material.needsUpdate = true;

        // Invalidate viewer to trigger re-render
        if (this.viewer && this.viewer.impl) {
            this.viewer.impl.invalidate(false, false, true);
        }
    }

    /**
     * Create a tag with dynamic circle icon
     * @param {Object} tagData - Tag data object
     * @param {string} color - Icon color (hex string like '#ff0000')
     * @param {string} hoverColor - Hover color (optional)
     * @returns {Object} Point cloud object
     */
    createDynamicCircleTag(tagData, color, hoverColor = null) {
        if (!tagData.point) return null;

        const iconOptions = this.createDynamicIconOptions(color, hoverColor);
        const geometry = this.getPointCloudTagGeometry(tagData, new THREE.Color(3.0, 3.0, 3.0));

        const pointCloud = this.initMesh_PointCloud_Tag(
            geometry,
            null, // No texture needed for shader-based icons
            null, // No hover texture needed
            true,
            this.getVisibilityRange(),
            false, // Not a cluster
            false, // Not large
            iconOptions
        );

        pointCloud.forgeData = [tagData];
        pointCloud.type = tagData.type || 'dynamic';
        pointCloud.isTag = true;
        pointCloud.isDynamicIcon = true;

        this.pointClouds.push(pointCloud);

        return pointCloud;
    }
}

Autodesk.Viewing.theExtensionManager.registerExtension('labels3dExt', labels3dExt);
